import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useInviteExternalToProject } from '../../hooks/useInviteExternalToProject';
import {
  useProjectExternalMembers,
  useRemoveProjectExternalMembers,
} from '../../hooks/useProjectExternalMembers';
import {
  suggestExternalUsers,
  type ExternalUserSuggestion,
} from '../../services/api/externalInvites';

export interface InviteExternalModalProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Callback invoked once the server returns success — caller renders a toast. */
  onSuccess?: (msg: string) => void;
  /** Callback invoked on server error — caller renders a toast. */
  onError?: (msg: string) => void;
}

// Tiny inline debounce — avoids pulling in a util just for this surface.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * PM-driven kanban modal for managing external members of a single project.
 *
 * Two stacked sections:
 *   1. Members — read-side list of active externals on this project; each
 *      row has a checkbox; "Remove selected" soft-deletes the underlying
 *      ExternalUserProject bindings server-side. Removal does NOT delete
 *      the external_user — they keep access to other projects they belong
 *      to.
 *   2. Invite — email input with a server-backed typeahead over existing
 *      external_users (matched on email / firstName / lastName). Picking a
 *      suggestion fills email + names so the subsequent send hits the
 *      existing-user branch (no invite email; just a project binding).
 *      Typing a new email falls through to the new-user branch — firstName
 *      + lastName are required there, server-side.
 */
export function InviteExternalModal({
  projectId,
  isOpen,
  onClose,
  onSuccess,
  onError,
}: InviteExternalModalProps) {
  const queryClient = useQueryClient();

  // --- Members section -----------------------------------------------------
  const membersQuery = useProjectExternalMembers(projectId, isOpen);
  const removeMutation = useRemoveProjectExternalMembers();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Reset checkbox state when the visible member list changes (e.g. after a
  // successful remove). The query key is stable so we key off the row count
  // + ids — cheap and correct.
  const memberIds = useMemo(
    () => (membersQuery.data ?? []).map((m) => m.id).join(','),
    [membersQuery.data],
  );
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(memberIds === '' ? [] : memberIds.split(',').map(Number));
      const next = new Set<number>();
      for (const id of prev) if (visible.has(id)) next.add(id);
      return next;
    });
  }, [memberIds]);

  function toggleSelected(id: number, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleRemoveSelected() {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    removeMutation.mutate(
      { projectId, externalUserIds: ids },
      {
        onSuccess: (data) => {
          const removedCount = data.removed.length;
          if (removedCount === 0) {
            if (onSuccess) onSuccess('Nothing to remove.');
            return;
          }
          if (onSuccess)
            onSuccess(
              removedCount === 1
                ? '1 user removed from project.'
                : `${removedCount} users removed from project.`,
            );
        },
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }

  // --- Invite section ------------------------------------------------------
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pickedSuggestion, setPickedSuggestion] = useState(false);
  const inviteMutation = useInviteExternalToProject();

  // Reset on close so the modal re-opens with a clean slate after a send.
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setPickedSuggestion(false);
      setSelectedIds(new Set());
      inviteMutation.reset();
      removeMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const debouncedEmail = useDebounced(email.trim(), 200);
  const suggestQuery = useQuery<ExternalUserSuggestion[], Error>({
    queryKey: ['external-invite-suggest', projectId, debouncedEmail],
    queryFn: () => suggestExternalUsers(projectId, debouncedEmail),
    enabled: isOpen && debouncedEmail.length >= 2 && !pickedSuggestion,
    staleTime: 30_000,
  });
  const suggestions = suggestQuery.data ?? [];
  const showSuggestions =
    isOpen &&
    !pickedSuggestion &&
    debouncedEmail.length >= 2 &&
    suggestions.length > 0;

  function pickSuggestion(s: ExternalUserSuggestion) {
    setEmail(s.email);
    setFirstName(s.firstName);
    setLastName(s.lastName);
    setPickedSuggestion(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) return;
    inviteMutation.mutate(
      {
        projectId,
        email: trimmedEmail,
        firstName: firstName.trim() === '' ? undefined : firstName.trim(),
        lastName: lastName.trim() === '' ? undefined : lastName.trim(),
      },
      {
        onSuccess: (data) => {
          const message =
            data.action === 'invited'
              ? `Invite sent to ${trimmedEmail}.`
              : data.action === 'added_to_project'
                ? `${trimmedEmail} added to the project.`
                : `${trimmedEmail} is already a member.`;
          if (onSuccess) onSuccess(message);
          // Refresh the members section so the new binding shows up without
          // requiring the user to re-open the modal.
          queryClient.invalidateQueries({
            queryKey: ['project-external-members', projectId],
          });
          // Clear only the invite-form state; keep the modal open so the PM
          // can immediately review the updated members list.
          setEmail('');
          setFirstName('');
          setLastName('');
          setPickedSuggestion(false);
        },
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }

  const canSubmit = email.trim().length > 0 && !inviteMutation.isPending;
  const members = membersQuery.data ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title="External users on this project">
      <div className="space-y-5">
        {/* ===================== Members section ===================== */}
        <section className="space-y-2">
          <header className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Current members</h3>
            <Button
              type="button"
              variant="danger"
              onClick={handleRemoveSelected}
              disabled={selectedIds.size === 0 || removeMutation.isPending}
              isLoading={removeMutation.isPending}
            >
              <TrashIcon className="w-4 h-4 mr-1 inline" />
              Remove selected ({selectedIds.size})
            </Button>
          </header>
          {membersQuery.isLoading ? (
            <p className="text-sm text-gray-400 italic">Loading members…</p>
          ) : membersQuery.isError ? (
            <p className="text-sm text-red-600">Couldn&rsquo;t load members.</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-400 italic">
              No external users on this project yet.
            </p>
          ) : (
            <ul className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-64 overflow-auto">
              {members.map((m) => {
                const fullName = [m.firstName, m.lastName].filter(Boolean).join(' ');
                const checked = selectedIds.has(m.id);
                return (
                  <li
                    key={m.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSelected(m.id, e.target.checked)}
                      aria-label={`Select ${m.email} for removal`}
                      className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-gray-800 truncate">{m.email}</span>
                      {fullName && (
                        <span className="text-xs text-gray-500 truncate">{fullName}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ===================== Invite section ===================== */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Invite or add a user</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-xs text-gray-500">
              Existing external users will be added directly. New emails get an invite email —
              provide first and last name in that case.
            </p>
            <div className="relative">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                Email
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setPickedSuggestion(false);
                  }}
                  placeholder="someone@example.com"
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="Email"
                  autoComplete="off"
                />
              </label>
              {showSuggestions && (
                <ul
                  role="listbox"
                  aria-label="Existing external users"
                  className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-md"
                >
                  {suggestions.map((s) => {
                    const fullName = [s.firstName, s.lastName].filter(Boolean).join(' ');
                    return (
                      <li key={s.id} role="option" aria-selected="false">
                        <button
                          type="button"
                          onClick={() => pickSuggestion(s)}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-primary-50"
                        >
                          <span className="font-medium text-gray-800">{s.email}</span>
                          {fullName && (
                            <span className="ml-2 text-xs text-gray-500">{fullName}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                First name (new users)
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="First name"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                Last name (new users)
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="text-sm font-medium text-gray-800 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  aria-label="Last name"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
              <Button type="button" variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!canSubmit}
                isLoading={inviteMutation.isPending}
              >
                Send
              </Button>
            </div>
          </form>
        </section>
      </div>
    </Modal>
  );
}
